// Transforms a real meeting + assessment into the MurphyReport shape the PDF
// expects (spec §12). Most fields come straight from the analyzer; a handful
// of murphyReview.* fields are derived here because the current analyzer
// doesn't emit them yet (see plan §5). When the analyzer eventually adds
// dedicated fields, point this mapper at them and drop the derivations.

import { neutralizeText } from './neutralizeText';
import { positiveRows, improvementRows, informationalRows } from '../../utils/reportColumns';

const STATUS_FROM_GRADE = {
  red: 'not-delivered',
  yellow: 'partial',
  green: 'delivered',
};

const STATUS_LABEL = {
  'not-delivered': 'Pitch not delivered',
  'partial': 'Partial delivery',
  'delivered': 'Pitch delivered',
  'not-applicable': 'Not applicable',
};

const STATUS_COLOR = {
  'not-delivered': 'red',
  'partial': 'amber',
  'delivered': 'green',
  'not-applicable': 'gray',
};

const FIT_LEVEL_LABEL = {
  strong: 'Strong fit',
  moderate: 'Moderate fit',
  weak: 'Weak fit',
  none: 'No fit',
};

const FIT_LEVEL_COLOR = {
  strong: 'green',
  moderate: 'amber',
  weak: 'red',
  none: 'gray',
};

const TARGET_FIT_COLOR = {
  excellent: 'green',
  good: 'green',
  moderate: 'amber',
  poor: 'red',
};

const capitalize = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1).toLowerCase() : s);
const truncateWords = (s, n) => {
  if (!s) return s;
  const words = String(s).split(/\s+/);
  return words.length <= n ? s : words.slice(0, n).join(' ') + '…';
};

const buildParticipants = (meeting) => {
  const list = (meeting?.participants && meeting.participants.length > 0)
    ? meeting.participants
    : (meeting?.validatedParticipants || []).map(email => ({ email, name: email, isMultiplyUser: false }));

  return list.map(p => ({
    name: p.name || p.email,
    role: p.role,
    company: p.company,
    isHost: !!p.isMultiplyUser,
  }));
};

const buildMurphyReview = (assessment, analysis) => {
  const grade = assessment?.grade;
  if (!grade && grade !== 0) return undefined;

  // Pull non-status fields (pitchType, pitchSubtitle, storyHeadline) from the
  // analyzer's murphyReview block. The status badge itself is driven by the
  // overall assessment grade, NOT by review.storyDeliveryStatus, so that the
  // PDF headline matches the dashboard's grade dot. storyDeliveryStatus
  // answers a narrower sub-question ("was the Who We Are pitch delivered?")
  // and disagreed with the overall grade on real-meeting reports, surprising
  // users who saw green in-app but yellow in the PDF for the same meeting.
  const review = analysis?.murphyReview || {};

  const status = STATUS_FROM_GRADE[grade] || 'not-applicable';
  const statusColor = STATUS_COLOR[status];
  const statusLabel = STATUS_LABEL[status];

  // covered/missing derived from pointsDescriptions through the shared column
  // util (utils/reportColumns.js) — the PDF previously reimplemented the 0.65
  // threshold WITHOUT the no-opportunity suppression, so rows hidden on the
  // report page still printed as red "missing" items in the downloaded PDF.
  const pd = Array.isArray(assessment.pointsDescriptions) ? assessment.pointsDescriptions : [];
  const covered = positiveRows(pd).map(p => p.description);
  const missing = improvementRows(pd).map(p => p.description);
  // Neutral delivery note (qa-family "Overall Delivery & Fluency"): in neither
  // column and never in the band, printed as a note (Toby, 2026-09-04).
  const delivery = informationalRows(pd).map(p => p.description);

  return {
    status,
    statusColor,
    statusLabel,
    delivery,
    pitchType: review.pitchType || 'Who We Are',
    pitchSubtitle: review.pitchSubtitle || 'the casual-encounter opener',
    score: typeof assessment.points === 'number' ? assessment.points : undefined,
    headline: review.storyHeadline || truncateWords(analysis?.summary?.overview, 50),
    covered,
    missing,
    coachingTips: analysis?.salesRepPerformance?.coachingTips || [],
  };
};

const buildAlignment = (analysis) => {
  const a = analysis?.problemAlignment || analysis?.painPointAlignment;
  if (!a) return undefined;
  const level = (a.fitLevel || 'none').toLowerCase();
  const matches = (Array.isArray(a.matches) ? a.matches : []).map(m => ({
    title: m.addressableProblem || m.title,
    quote: m.prospectStatement || m.quote,
    attribution: m.speaker && m.speaker !== 'unknown' ? m.speaker : '',
    evidence: capitalize(m.evidenceStrength || ''),
    offerings: Array.isArray(m.matchingOfferings) ? m.matchingOfferings : [],
    nextStep: m.salesAction,
  }));
  return {
    fitLabel: FIT_LEVEL_LABEL[level] || 'No fit',
    fitColor: FIT_LEVEL_COLOR[level] || 'gray',
    lead: a.summary,
    matches,
  };
};

// Shared variant: strip rep-perspective `type` taxonomy and the
// `significance` coaching field; neutralize description prose so it
// matches the inline /share/{token} payload built by handler.js.
const buildMoments = (analysis, isShared) =>
  (Array.isArray(analysis?.keyMoments) ? analysis.keyMoments : []).map(m => ({
    timestamp: m.timestamp || '',
    type: isShared ? undefined : capitalize(m.type || ''),
    description: isShared ? neutralizeText(m.description || '') : m.description,
    whyItMatters: isShared ? undefined : m.significance,
  }));

const buildOpportunities = (analysis) =>
  (Array.isArray(analysis?.opportunities) ? analysis.opportunities : []).map(o => ({
    type: capitalize(o.type || ''),
    description: o.description,
    recommendation: o.recommendation,
    whyWeFit: o.companyAlignment,
  }));

const buildRisks = (analysis) =>
  (Array.isArray(analysis?.risks) ? analysis.risks : []).map(r => ({
    type: capitalize(r.type || ''),
    severity: (r.severity || '').toLowerCase(),
    description: r.description,
    mitigation: r.mitigation,
  }));

// Shared variant: drop `owner` (analyzer hard-codes it to the salesperson)
// and `priority` (rep-internal triage). Neutralize action prose.
const buildNextSteps = (analysis, isShared) =>
  (Array.isArray(analysis?.nextSteps) ? analysis.nextSteps : []).map(s => ({
    action: isShared ? neutralizeText(s.action || '') : s.action,
    owner: isShared ? undefined : s.owner,
    when: s.timeline,
    priority: isShared ? undefined : (s.priority || '').toLowerCase(),
  }));

const buildProfile = (analysis) => {
  const p = analysis?.prospectProfile;
  if (!p) return undefined;
  const fitLevel = (p.targetMarketFit?.level || p.targetMarketFit || '').toLowerCase();
  return {
    painPoints: Array.isArray(p.painPoints) ? p.painPoints : [],
    buyingSignals: Array.isArray(p.buyingSignals) ? p.buyingSignals : [],
    decisionCriteria: Array.isArray(p.decisionCriteria) ? p.decisionCriteria : [],
    targetMarketFit: fitLevel ? {
      level: capitalize(fitLevel),
      color: TARGET_FIT_COLOR[fitLevel] || 'amber',
      reasoning: p.fitReasoning || p.targetMarketFit?.reasoning,
    } : undefined,
  };
};

const buildSalesRepPerformance = (analysis) => {
  const perf = analysis?.salesRepPerformance;
  if (!perf) return undefined;
  if (typeof perf === 'string') return { overallAssessment: perf, strengths: [], areasForImprovement: [], coachingTips: [] };
  return {
    overallAssessment: perf.overallAssessment,
    strengths: Array.isArray(perf.strengths) ? perf.strengths : [],
    areasForImprovement: Array.isArray(perf.areasForImprovement) ? perf.areasForImprovement : [],
    coachingTips: Array.isArray(perf.coachingTips) ? perf.coachingTips : [],
  };
};

const buildQa = (assessment) => {
  const qaItems = assessment?.qaAnalysis?.questionAnalysis;
  if (!Array.isArray(qaItems)) return [];
  // Live-meeting Q&A is transcript context, not training — carry the speaker-
  // labeled shape. Items written before the schema split lack askedBy /
  // answer / speakers; fall back so old PDFs still render (default askedBy
  // to 'prospect' — the pre-split assumption).
  return qaItems.map(q => ({
    askedBy: q.askedBy === 'seller' ? 'seller' : 'prospect',
    question: q.question || '',
    questionSpeaker: q.questionSpeaker || '',
    answer: q.answer || q.userResponse || '',
    answerSpeaker: q.answerSpeaker || '',
  }));
};

/**
 * @param {Object} input
 * @param {Object} input.meeting     The MyMeetings meeting object (botId, meetingTitle, etc.)
 * @param {Object} input.assessment  meeting.assessment — Live Meeting assessment
 * @param {'internal'|'shared'} input.variant
 * @returns {Object} MurphyReport
 */
export default function mapToReport({ meeting, assessment, variant = 'internal' }) {
  const analysis = assessment?.liveMeetingAnalysis;
  const isShared = variant === 'shared';

  const report = {
    variant,
    meeting: {
      title: meeting?.meetingTitle || 'Untitled Meeting',
      dateISO: meeting?.meetingDate,
      durationMinutes: meeting?.duration ? Math.round(meeting.duration / 60) : undefined,
      type: capitalize(analysis?.summary?.meetingType || ''),
      dealStage: isShared ? '' : capitalize(analysis?.summary?.dealStage || ''),
      sentiment: isShared ? '' : capitalize(analysis?.summary?.overallSentiment || ''),
      participants: buildParticipants(meeting),
    },
    // Shared variant prefers the analyzer's purpose-built neutralSummary;
    // older assessments without that field fall back to a regex-stripped
    // version of the rep-POV overview — mirrors handler.js:113-115.
    summary: isShared
      ? (analysis?.neutralSummary && analysis.neutralSummary.trim()
          ? neutralizeText(analysis.neutralSummary)
          : neutralizeText(analysis?.summary?.overview || ''))
      : analysis?.summary?.overview,
    alignment: buildAlignment(analysis),
    moments: buildMoments(analysis, isShared),
    opportunities: buildOpportunities(analysis),
    risks: buildRisks(analysis),
    nextSteps: buildNextSteps(analysis, isShared),
    salesRepPerformance: buildSalesRepPerformance(analysis),
    profile: buildProfile(analysis),
    qa: buildQa(assessment),
  };

  // Internal variant gets the Murphy review block + coaching surfaces; shared
  // strips them so the prospect doesn't see rep critique.
  if (!isShared) {
    report.murphyReview = buildMurphyReview(assessment, analysis);
  }

  return report;
}
